import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { useT } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Server-signed math CAPTCHA. Renders a question fetched from /api/captcha and
 * exposes the current { answer, token } to the parent via the `onChange` callback.
 *
 * Parent usage:
 *   const [captcha, setCaptcha] = useState({ answer: "", token: "" });
 *   <Captcha value={captcha} onChange={setCaptcha} refreshKey={refreshSignal} />
 *
 *   Then include captcha.answer + captcha.token in the payload as
 *   `captcha_answer` and `captcha_token`.
 */
export default function Captcha({ value, onChange, refreshKey = 0, className = "" }) {
  const t = useT();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/captcha`);
      setQuestion(res.data.question);
      onChange({ answer: "", token: res.data.token });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleAnswerChange = (e) => {
    onChange({ ...value, answer: e.target.value });
  };

  return (
    <div className={`bg-cream-50 border border-cream-100 rounded-2xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-brand" />
        <label className="text-xs uppercase tracking-widest text-granite/70 font-semibold">
          {t("Quick human check", "Verificación rápida")}
        </label>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 flex items-center gap-3">
          <div className="font-display text-2xl font-semibold text-granite-dark min-w-[110px]">
            {loading ? "…" : error ? "?" : `${question} =`}
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[-0-9]*"
            value={value?.answer || ""}
            onChange={handleAnswerChange}
            placeholder="?"
            disabled={loading || error}
            className="w-24 px-4 py-2.5 rounded-xl bg-cream border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-center text-lg font-semibold text-granite-dark"
            aria-label="Captcha answer"
          />
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="p-2 rounded-full hover:bg-cream text-granite/60 hover:text-brand transition-colors disabled:opacity-50"
          aria-label="Refresh captcha"
          title="New question"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {error && (
        <div className="text-xs text-brand mt-2">
          {t("Couldn't load the check. Click refresh to try again.", "No pudimos cargar la verificación. Haz clic en refrescar para reintentar.")}
        </div>
      )}
    </div>
  );
}
